import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { wrapComponent } from '@components/DynamicSnackBar';
import { Page } from '@containers/layout';
import {
  DiscontinueType,
  DiscontinueDate,
  DiscontinueConfirm
} from '@containers/discontinue';
import { Box, IconButton } from '@components/common';
import FullScreenLoader from '@components/loading/FullScreenLoader';

import DiscontinueActions, {
  DISCONTINUE_STAGE,
  DiscontinueSelectors
} from '@redux/DiscontinueRedux';
import SubscriptionActions, {
  SubscriptionSelectors
} from '@redux/SubscriptionRedux';

import history from '@utils/history';

const MobileFlex = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: 'column';
  width: 100%;
  flex: 1;
  justify-content: center;
  align-items: center;

  @media screen and (min-width: 1024px) {
    flex-direction: column;
    justify-content: flex-start;
    padding-top: 48px;
  }

  @media screen and (max-width: 767px) {
    flex-direction: column;
    justify-content: center;
  }
`;

function DiscontinuePage(props) {
  const {
    activeSubscription,
    isPause,
    discontinueStep,
    initialStep,
    goToStep,
    resetDiscontinue,
    loadSubscriptions,
    loading,
    createSnackbar
  } = props;

  const goToNextStep = (pauseFlow = false, isMoving = false) => {
    switch (discontinueStep) {
      case DISCONTINUE_STAGE.TYPE:
        if (pauseFlow) {
          goToStep(DISCONTINUE_STAGE.DATE);
        } else {
          goToStep(DISCONTINUE_STAGE.CONFIRM);
        }
        break;
      case DISCONTINUE_STAGE.DATE:
        goToStep(DISCONTINUE_STAGE.CONFIRM);
        break;
      case DISCONTINUE_STAGE.CONFIRM:
        createSnackbar({
          message: `Service is successfully ${
            isPause ? 'paused' : 'discontinued'
          }`,
          theme: 'success'
        });
        if (pauseFlow) {
          history.goBack();
        } else {
          if (isMoving) {
            history.push('/moveout');
          } else {
            history.push('/home');
          }
        }
        break;
      default:
    }
  };

  const goToPrevStep = () => {
    switch (discontinueStep) {
      case DISCONTINUE_STAGE.TYPE:
        history.goBack();
        break;
      case DISCONTINUE_STAGE.DATE:
        if (initialStep === DISCONTINUE_STAGE.DATE) {
          history.goBack();
        } else {
          goToStep(DISCONTINUE_STAGE.TYPE);
        }
        break;
      case DISCONTINUE_STAGE.CONFIRM:
        if (isPause) {
          goToStep(DISCONTINUE_STAGE.DATE);
        } else {
          goToStep(DISCONTINUE_STAGE.TYPE);
        }
        break;
      default:
    }
  };

  const renderContent = () => {
    switch (discontinueStep) {
      case DISCONTINUE_STAGE.TYPE:
        return (
          <DiscontinueType onSubmit={goToNextStep} onCancel={goToPrevStep} />
        );
      case DISCONTINUE_STAGE.DATE:
        return (
          <DiscontinueDate onSubmit={goToNextStep} onCancel={goToPrevStep} />
        );
      case DISCONTINUE_STAGE.CONFIRM:
        return (
          <DiscontinueConfirm onSubmit={goToNextStep} onCancel={goToPrevStep} />
        );
    }
    return null;
  };

  useEffect(() => {
    if (!activeSubscription) {
      loadSubscriptions();
    }
    return () => {
      resetDiscontinue();
    };
  }, []);

  if (loading) {
    return <FullScreenLoader />;
  }

  const renderBackButton = () => {
    return (
      <IconButton
        onClick={goToPrevStep}
        name="back"
        width={16}
        height={16}
        title="Back"
      />
    );
  };

  return (
    <Page
      renderHeaderLeftButtons={renderBackButton}
      title={isPause ? 'Pause' : 'Discontinue'}
    >
      <MobileFlex>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          flex={1}
          width={1}
        >
          {renderContent()}
        </Box>
      </MobileFlex>
    </Page>
  );
}

DiscontinuePage.propTypes = {
  initialStep: PropTypes.string,
  discontinueStep: PropTypes.string,
  resetDiscontinue: PropTypes.func.isRequired,
  goToStep: PropTypes.func.isRequired,
  isPause: PropTypes.bool,
  activeSubscription: PropTypes.any,
  loadSubscriptions: PropTypes.func,
  loading: PropTypes.bool,
  createSnackbar: PropTypes.func
};

const mapStatesToProps = state => ({
  discontinueStep: DiscontinueSelectors.selectCurrentStep(state),
  initialStep: DiscontinueSelectors.selectInitialStep(state),
  isPause: DiscontinueSelectors.selectIsPause(state),
  loading: SubscriptionSelectors.selectLoading(state),
  activeSubscription: SubscriptionSelectors.selectActiveSubscription(state)
});

const mapDispatchToProps = dispatch => ({
  resetDiscontinue: () => dispatch(DiscontinueActions.resetDiscontinue()),
  goToStep: step => dispatch(DiscontinueActions.setDiscontinueStep(step)),
  loadSubscriptions: () =>
    dispatch(SubscriptionActions.setSubscriptionLoadRequest())
});

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(wrapComponent(DiscontinuePage));
